import React from "react";
import { PlusMinus } from "@/components/PlusMinus";
import { PriceDisplay } from "@/features/product/PriceDisplay";
import { useCartUiActions } from "./cartStore";
import {
  cartItemSelect,
  shopifyCartApi,
  ShopifyCartItem,
} from "@/services/shopifyCart";
import { ProductCard } from "../product/ProductCard";
import { SizedImage } from "@/components/SizedImage";
import { giftWithPurchase } from "@/helpers/data/cartGiftProductsData";
import { Money, $ } from "@/helpers/Money";
import { DroppsProduct, productSelect, variantSelect } from "@/store";
import { Page } from "./CartDrawer";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TagIcon } from "@/components/icons";
import {
  getEffectiveSubscriptionDiscountPercent,
  getFreeShippingBundleProductIds,
} from "@/helpers/constants";
import { useGlobalMetafields } from "@/services/globalMetafields";

interface CartProductProps {
  item: ShopifyCartItem & { shopifyProduct?: DroppsProduct };
  setOpenDrawer: (page: Page) => void;
}

const subscriptionText = (item: ShopifyCartItem) => {
  const subscription = cartItemSelect.subscription(item);
  return subscription.kind === "selling-plan"
    ? subscription.name
    : "One-Time Purchase";
};

export const CartProduct = ({ item, setOpenDrawer }: CartProductProps) => {
  const { closeDrawer } = useCartUiActions();
  const cartUpdater = shopifyCartApi.useChange();
  const cartUpdate = shopifyCartApi.useUpdate();
  const shopifyCart = shopifyCartApi.useFetch();
  const cart = shopifyCart.data;
  const { data: globalMetafields } = useGlobalMetafields();

  // Check if this item is a gift with purchase (GWP) item
  const isGwpItem = item.properties?.["_gwp"] == "true";

  // Get free shipping bundle product IDs from global metafield
  const bundleProductIds = getFreeShippingBundleProductIds(globalMetafields);

  // Helper function to handle bundle item removal
  const handleBundleRemoval = async (itemToRemove: ShopifyCartItem) => {
    if (!cart) {
      return cartUpdater.mutateAsync({ id: itemToRemove.key, quantity: 0 });
    }

    // Find all other bundle items in cart
    const otherBundleItems = cart.items.filter(
      (cartItem) =>
        bundleProductIds.includes(cartItem.product_id) &&
        cartItem.product_id !== itemToRemove.product_id
    );

    if (otherBundleItems.length > 0) {
      // Remove all bundle items in a single API call
      const updates = otherBundleItems.reduce(
        (acc, cartItem) => {
          acc[cartItem.key] = 0;
          return acc;
        },
        { [itemToRemove.key]: 0 } as Record<string, number>
      );

      return cartUpdate.mutateAsync(updates);
    } else {
      // No other bundle items, just remove this one
      return cartUpdater.mutateAsync({ id: itemToRemove.key, quantity: 0 });
    }
  };

  const getStandardPrice = () => {
    const defaultVariant = item?.shopifyProduct?.variants.find(
      (variant) => variant.default_variant_multiplier === 1
    );

    const selectedVariant =
      item.shopifyProduct &&
      productSelect.selectedVariant(item.shopifyProduct, item.variant_id);
    if (selectedVariant) {
      return selectedVariant.price.mul(item.quantity);
    }
    // fallback if no variant data
    return item.price.mul(item.quantity);
  };

  const productPageUrl = cartItemSelect.productUrl(item);
  const { active: gwpActive, products: gwpProducts } = giftWithPurchase;
  const promoIds = gwpProducts.map((product) => product.variantId);
  const isPromoGift = promoIds.includes(item.variant_id);
  const limitPerOrder = gwpActive && isPromoGift;
  const activePromo = limitPerOrder && item.quantity <= 1;
  const cartValue = cart?.items_subtotal_price ?? Money.zero;

  const baseStandardPrice = getStandardPrice();

  const selectedVariant =
    item.shopifyProduct &&
    productSelect.selectedVariant(item.shopifyProduct, item.variant_id);

  const sellingPlanAllocation = item?.selling_plan_allocation;
  const discountedPriceCents = item?.discounted_price;

  // Follow PDP logic for compare-at display in cart.
  const unitCompareAtPrice =
    selectedVariant &&
    selectedVariant.compareAtPrice &&
    selectedVariant.compareAtPrice.asCents() > selectedVariant.price.asCents()
      ? selectedVariant.compareAtPrice
      : sellingPlanAllocation?.compare_at_price != null
      ? Money.from(sellingPlanAllocation.compare_at_price, "¢")
      : selectedVariant?.price ?? item.price;
  const compareAtLinePrice = unitCompareAtPrice.mul(item.quantity);
  const discountedPrice =
    discountedPriceCents != null
      ? Money.from(discountedPriceCents.asCents(), "¢").mul(item.quantity)
      : null;

  const hasCompareAtDiscount =
    item.final_line_price.asCents() < compareAtLinePrice.asCents();
  const hasCustomDiscount =
    discountedPrice &&
    discountedPrice.asCents() < item.original_line_price.asCents();

  const hasDiscount = !!(hasCompareAtDiscount || hasCustomDiscount);

  const displayStandardPrice = hasDiscount
    ? compareAtLinePrice
    : baseStandardPrice;
  const podCountFromSizeLabel = (() => {
    // Fallback for cases where cart variant_id doesn't map to shopifyProduct variants
    // (e.g. subscription variant ids, tiered/base variant mapping)
    const sizeLabel = cartItemSelect.sizing(item);
    const match = sizeLabel.match(/(\d+)/);
    return match ? Number(match[1]) : undefined;
  })();
  const unitPodCount =
    selectedVariant?.package_quantity ?? podCountFromSizeLabel;
  const totalPodCount = (unitPodCount ?? 0) * item.quantity;

  const cartItem = {
    title: item.product_title,
    featuredImage: {
      src: item.image,
      alt: item.title,
      width: 80,
      height: 80,
    },
    size: selectedVariant
      ? variantSelect.sizeFormat(selectedVariant)
      : cartItemSelect.sizing(item),
    // Cart design: hide default subscription cadence text (e.g. "Delivery every 4 Months")
    // but still show promo gift messaging when applicable.
    subscription:
      activePromo && cartValue && cartValue >= $`30`
        ? "Free with $30+ order"
        : undefined,
  };

  const sellingPlan = sellingPlanAllocation?.selling_plan;

  // Derive product flags for bulk/bundle exclusions
  const droppsProduct = item.shopifyProduct;
  const isBulk = droppsProduct ? productSelect.isBulk(droppsProduct) : false;
  const isBundle = Boolean(
    droppsProduct &&
      (droppsProduct.type === "Bundle" ||
        droppsProduct.bundledItems?.length ||
        droppsProduct.tags?.includes("bundle"))
  );
  const isTrialProduct = droppsProduct?.tags?.includes("trial-product");

  const multipleBoxDiscount = (() => {
    const hasBuyMoreSaveMoreTitle = (title: unknown) =>
      typeof title === "string" &&
      title.toLowerCase().includes("buy more save more");

    // Prefer allocation-level metadata when available (it contains value/value_type)
    const allocations = ((item as any).line_level_discount_allocations ??
      []) as Array<{
      amount?: number;
      discount_application?: {
        title?: string;
        value_type?: string;
        value?: string | number;
      };
    }>;

    const allocation = allocations.find((x) =>
      hasBuyMoreSaveMoreTitle(x?.discount_application?.title)
    );
    if (allocation?.discount_application?.value_type === "percentage") {
      const value = Number(allocation.discount_application.value);
      if (!Number.isNaN(value) && value > 0) {
        return value;
      }
    }

    // Fallback to cart discount list and compute % from amount if needed
    const discounts = ((item as any).discounts ?? []) as Array<{
      amount?: number;
      title?: string;
    }>;
    const discount = discounts.find((x) => hasBuyMoreSaveMoreTitle(x?.title));
    if (
      discount &&
      typeof discount.amount === "number" &&
      item.original_line_price.asCents() > 0
    ) {
      const value = Math.round(
        (discount.amount / item.original_line_price.asCents()) * 100
      );
      if (!Number.isNaN(value) && value > 0) {
        return value;
      }
    }

    return undefined;
  })();
  const subscriptionDiscount =
    sellingPlan?.price_adjustments?.length && sellingPlan.price_adjustments[0]
      ? getEffectiveSubscriptionDiscountPercent(
          sellingPlan.price_adjustments[0].value,
          {
            isBulk,
            isBundle,
          }
        )
      : null;

  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between">
        <ProductCard
          compact
          hoverable={false}
          rounded={false}
          fullwidth
          productCardValue={cartItem}
          isTrialProduct={isTrialProduct}
          cover={
            cartItem.featuredImage?.src ? (
              <a href={productPageUrl} onClick={closeDrawer}>
                <div className="w-20 shrink-0 [&>img]:max-w-full">
                  <SizedImage
                    src={cartItem.featuredImage.src}
                    alt={cartItem.title}
                    height={80}
                    width={80}
                    loading="lazy"
                  />
                </div>
              </a>
            ) : undefined
          }
          productTitle={
            <a href={productPageUrl} onClick={closeDrawer}>
              <h3
                data-id="product-card-title"
                className="title text-sm transition hover:underline"
              >
                {productSelect.name(cartItem)}
              </h3>
            </a>
          }
        />
      </div>
      <div className="flex flex-col px-5 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <PlusMinus
              value={item.quantity}
              decrement={async () => {
                const newQuantity = Math.max(0, item.quantity - 1);

                if (newQuantity === 0) {
                  // Only handle bundle removal if this item is a bundle item
                  if (bundleProductIds.includes(item.product_id)) {
                    await handleBundleRemoval(item);
                  } else {
                    // Normal item removal
                    await cartUpdater.mutateAsync({
                      id: item.key,
                      quantity: newQuantity,
                    });
                  }
                } else {
                  // Normal quantity update
                  cartUpdater.mutate({ id: item.key, quantity: newQuantity });
                }
              }}
              increment={() => {
                const newQuantity = item.quantity + 1;
                cartUpdater.mutate({ id: item.key, quantity: newQuantity });
              }}
              enableTrash={true}
              disabled={isGwpItem}
              min={1}
            />
            {item.quantity > 1 && totalPodCount > 0 && (
              <span className="ml-[18px] whitespace-nowrap rounded-[20px] bg-secondary-200 px-[6px] py-[2px] text-[10px] font-medium leading-[13px] text-black">
                {totalPodCount} total pods
              </span>
            )}
          </div>
          <div className="flex flex-col items-end">
            <PriceDisplay
              size="cart"
              standardPrice={displayStandardPrice}
              discountPrice={
                hasDiscount
                  ? (() => {
                      // Prioritize line_price or final_line_price as they already include ALL discounts
                      // (both subscription discounts and tiered discounts from Recharge)
                      if (
                        item.line_price &&
                        item.line_price.asCents() <
                          item.original_line_price.asCents()
                      ) {
                        return item.line_price;
                      }
                      if (
                        item.final_line_price &&
                        item.final_line_price.asCents() <
                          item.original_line_price.asCents()
                      ) {
                        return item.final_line_price;
                      }
                      // Use global discount utility for consistent pricing if line prices not available
                      if (
                        sellingPlan &&
                        sellingPlan.price_adjustments &&
                        sellingPlan.price_adjustments.length > 0
                      ) {
                        const priceAdjustment =
                          sellingPlan.price_adjustments[0];
                        if (priceAdjustment.value_type === "percentage") {
                          const percent =
                            getEffectiveSubscriptionDiscountPercent(
                              priceAdjustment.value,
                              { isBulk, isBundle }
                            );
                          // Calculate discounted unit price first, then multiply by quantity (consistent with cart subtotal)
                          const unitCompareAtPrice =
                            sellingPlanAllocation?.compare_at_price
                              ? Money.from(
                                  sellingPlanAllocation.compare_at_price,
                                  "¢"
                                )
                              : item.price;
                          const discountedUnitPrice = unitCompareAtPrice.mul(
                            1 - percent / 100
                          );
                          return discountedUnitPrice.mul(item.quantity);
                        }
                      }
                      // Fallback to backend price if no selling plan discount
                      return (
                        (item.discounted_price &&
                          item.discounted_price.mul(item.quantity)) ||
                        item.final_line_price
                      );
                    })()
                  : undefined
              }
              showDiscountPrice={hasDiscount}
            />
          </div>
        </div>
        {(multipleBoxDiscount || subscriptionDiscount) && (
          <div className="mt-3 space-y-1">
            {multipleBoxDiscount ? (
              <p className="mb-0 flex items-center gap-1 text-[12px] font-normal leading-[14px] text-black">
                <TagIcon sx={{ width: 14, height: 14, color: "#00847B" }} />
                <span>
                  <span className="font-bold text-[#00847B]">
                    {multipleBoxDiscount}% OFF
                  </span>{" "}
                  multiple box discount
                </span>
              </p>
            ) : null}
            {subscriptionDiscount ? (
              <p className="mb-0 flex items-center gap-1 text-[12px] font-normal leading-[14px] text-black">
                <TagIcon sx={{ width: 14, height: 14, color: "#00847B" }} />
                <span>
                  <span className="font-bold text-[#00847B]">
                    {subscriptionDiscount}% OFF
                  </span>{" "}
                  subscription discount
                </span>
              </p>
            ) : null}
          </div>
        )}
        {item.shopifyProduct?.bundledItems?.length && (
          <div
            role="button"
            tabIndex={0}
            className="flex cursor-pointer items-center pt-1 underline"
            onKeyDown={() =>
              setOpenDrawer({
                kind: "bundle-drawer",
                bundledProducts: item.shopifyProduct?.bundledItems ?? [],
                item,
              })
            }
            onClick={() => {
              setOpenDrawer({
                kind: "bundle-drawer",
                bundledProducts: item.shopifyProduct?.bundledItems ?? [],
                item,
              });
            }}
          >
            <p className="mb-0 text-sm">What&apos;s included</p>
            <KeyboardArrowDownIcon />
          </div>
        )}
      </div>
    </div>
  );
};
